/**
 * Categorias da Central de notificações (SPEC-20260924-2111).
 *
 * Os rótulos são os mesmos chips do explorer (`shell/modules.ts`, módulo
 * `notifications`) — é por eles que a tela filtra, e o chip "Todas" não é
 * categoria: é a ausência de filtro.
 *
 * Hoje só `work_item_assigned` chega do servidor. As outras categorias existem
 * porque os chips já as oferecem e cada novo `kind` entra aqui sem tocar a
 * tela; até lá elas simplesmente não têm linha nenhuma.
 */

export type CategoriaDeNotificacao =
  | 'Menções'
  | 'Atribuições'
  | 'Convites'
  | 'Prazos'
  | 'Mudanças'
  | 'Automações'
  | 'E-mails'

export type MetaDeCategoria = {
  id: CategoriaDeNotificacao
  icon: string
  /** Cor do ícone. Sempre acompanhada do rótulo textual da categoria. */
  tone: 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral'
}

export const META_DAS_CATEGORIAS: readonly MetaDeCategoria[] = [
  { id: 'Menções', icon: 'fas fa-at', tone: 'primary' },
  { id: 'Atribuições', icon: 'fas fa-user-check', tone: 'info' },
  // convite para evento do Calendário (SPEC-20261027-2350-calendario-real)
  { id: 'Convites', icon: 'far fa-calendar-check', tone: 'primary' },
  { id: 'Prazos', icon: 'fas fa-clock', tone: 'danger' },
  { id: 'Mudanças', icon: 'fas fa-exchange-alt', tone: 'neutral' },
  { id: 'Automações', icon: 'fas fa-bolt', tone: 'success' },
  { id: 'E-mails', icon: 'far fa-envelope', tone: 'warning' },
]

export const metaDaCategoria = (categoria: CategoriaDeNotificacao): MetaDeCategoria =>
  META_DAS_CATEGORIAS.find((meta) => meta.id === categoria) ?? META_DAS_CATEGORIAS[0]!

/** Em que chip a notificação cai. `kind` desconhecido vira "Mudanças". */
export const categoriaDoKind = (kind: string): CategoriaDeNotificacao =>
  kind === 'work_item_assigned' || kind === 'work_items_inherited'
    ? 'Atribuições'
    : kind === 'calendar_invite'
      ? 'Convites'
      : 'Mudanças'

/** A frase que a linha mostra, já com o sujeito da ação. */
export const fraseDaNotificacao = (n: { kind: string; autor: string }): string =>
  n.kind === 'work_item_assigned'
    ? `${n.autor} atribuiu este Item de Trabalho a você.`
    : // em lote, pela raiz (SPEC-20261103-2144): um aviso por raiz, não por Item
      n.kind === 'work_items_inherited'
      ? `${n.autor} tornou você responsável pelos Itens sem dono abaixo deste Item, que é seu.`
      : n.kind === 'calendar_invite'
      ? `${n.autor} convidou você para este evento.`
      : `${n.autor} mexeu neste Item de Trabalho.`
